import { FC, useCallback, useMemo } from 'react';
import { AdElementImage, BrandVariable } from '@adflow/types';
import { isBrandLogoVariable } from '@adflow/variable-resolvers';
import { Flex, Text, RadioGroup, Radio, Stack, Box } from '@chakra-ui/react';
import DynamicContentTemplateInput from './DynamicContentTemplateInput';
import BrandBadge from './BrandBadge';
import { useStore } from '../../hooks/useStore';

type Props = {
  selectedElement: AdElementImage;
};

const ImageElementControls: FC<Props> = ({ selectedElement }) => {
  const { brandKit, updateElementData } = useStore((state) => ({
    brandKit: state.brandKit,
    updateElementData: state.updateElementData,
  }));

  const hasBrandLogo = useMemo(() => {
    return !!brandKit?.logoUrl;
  }, [brandKit?.logoUrl]);

  const isUsingBrandLogo = useMemo(() => {
    if (!hasBrandLogo) return false;
    return isBrandLogoVariable(selectedElement.data.content);
  }, [hasBrandLogo, selectedElement.data.content]);

  const handleModeChange = useCallback(
    (value: string) => {
      if (value === 'brand') {
        updateElementData(selectedElement.id, {
          ...selectedElement.data,
          content: BrandVariable.LOGO,
        });
      } else if (value === 'static') {
        updateElementData(selectedElement.id, {
          ...selectedElement.data,
          content: '',
        });
      }
    },
    [selectedElement, updateElementData],
  );

  return (
    <Flex direction="column">
      {hasBrandLogo && (
        <>
          <RadioGroup
            value={isUsingBrandLogo ? 'brand' : 'static'}
            onChange={handleModeChange}
            mb={4}
          >
            <Stack direction="column" spacing={2}>
              <Radio value="brand">
                <Flex alignItems="center" gap={2}>
                  Use brand logo
                  <BrandBadge variableType={BrandVariable.LOGO} />
                </Flex>
              </Radio>
              <Radio value="static">Use static image</Radio>
            </Stack>
          </RadioGroup>
        </>
      )}

      {(!isUsingBrandLogo || !hasBrandLogo) && (
        <Box>
          <DynamicContentTemplateInput selectedElement={selectedElement} />
          <Text mt={2} fontStyle={'italic'} fontSize={'sm'}>
            Provide a link to the image you want to display in the ad. You can use the dynamic
            content template to insert dynamic data into the image URL. For example:
            https://cdn.fresh8.co/assets/operator-jerseys/tipico-us/&#123;FOCUS_COMPETITOR_ID-CF&#125;_Left.png
          </Text>
        </Box>
      )}
    </Flex>
  );
};

ImageElementControls.displayName = 'ImageElementControls';

export default ImageElementControls;
